import {
  Box,
  FormattedDateTime,
  Panel,
  PanelSection,
  ProgressBar,
  Stack,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import { agentUsedPercent, useAgentUsageMeter, type AgentUsageMeter } from '@/features/billing-usage';

/**
 * Chat surfaces stay clean below this percent of included usage; at or past it
 * the input pill and in-thread card appear. 75 vs 78 is still open with product —
 * the earlier trigger is the safer default and this is the only line to change.
 */
export const AGENT_USAGE_SIGNAL_PERCENT = 75;

const PILL_BAR_STYLE = { inlineSize: '3.5rem' };

type AgentUsageSignal = {
  meter: AgentUsageMeter;
  /** Derived from raw credits, so it keeps counting past 100. */
  percent: number;
};

/**
 * The single gate behind both chat usage surfaces. Fails closed: no meter
 * (flag off, load error, still loading) or usage below the threshold means
 * the chat renders exactly as it does today.
 */
export function useAgentUsageSignal(): AgentUsageSignal | null {
  const { meter } = useAgentUsageMeter();
  if (meter == null) {
    return null;
  }
  const percent = agentUsedPercent(meter);
  if (percent < AGENT_USAGE_SIGNAL_PERCENT) {
    return null;
  }
  return { meter, percent };
}

/**
 * The compact usage pill in the chat input's control row: mini bar plus the
 * used percent, with the full explanation in a tooltip. Text turns critical
 * once the included amount is crossed — during the beta nothing blocks.
 */
export function AgentUsagePill() {
  const signal = useAgentUsageSignal();
  if (signal == null) {
    return null;
  }
  const { percent } = signal;
  const reached = percent >= 100;

  const tooltipContent = reached
    ? `You've used ${percent}% of your included usage of the Agent this cycle. Nothing is blocked, and you won't be charged.`
    : `You've used ${percent}% of your included usage of the Agent this cycle. We're not enforcing limits, and you won't be charged.`;

  return (
    <Tooltip content={tooltipContent} isCustom={true}>
      {(context) => (
        <Box
          display="flex"
          alignItems="center"
          gap="050"
          data-test-agent-usage-pill
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
        >
          <Box style={PILL_BAR_STYLE}>
            <ProgressBar
              value={Math.min(percent, 100)}
              ariaLabel={`${percent}% of the Agent's included usage`}
              size="sm"
            />
          </Box>
          <Text size="xs" color={reached ? 'critical' : 'subtle'} weight={reached ? 'medium' : undefined}>
            {percent}% of included usage
          </Text>
        </Box>
      )}
    </Tooltip>
  );
}

type AgentUsageThreadCardProps = {
  signal: AgentUsageSignal;
};

/**
 * The in-thread usage card, shown at the bottom of the conversation once the
 * threshold is crossed. Deliberately light: no dismiss state, no modal.
 */
export function AgentUsageThreadCard({ signal }: AgentUsageThreadCardProps) {
  const { meter, percent } = signal;
  const reached = percent >= 100;

  return (
    <Panel data-test-agent-usage-thread-card>
      <PanelSection>
        <Stack gap="100">
          <Text weight="semibold">{reached ? 'Included usage reached' : 'Included usage approaching'}</Text>
          <ProgressBar
            value={Math.min(percent, 100)}
            ariaLabel={`${percent}% of the Agent's included usage`}
            size="sm"
          />
          <Text size="s" color="subtle" data-test-agent-usage-thread-reset>
            Your included usage resets on{' '}
            <FormattedDateTime withTooltip={false} date={new Date(meter.reset_date).getTime()} format="medium" />.
          </Text>
          <Text size="s" color="subtle">
            Nothing changes today. We&apos;re still working out pricing for the Agent, and you won&apos;t be charged.
          </Text>
        </Stack>
      </PanelSection>
    </Panel>
  );
}
